// src/components/SaveEstimateModal.js

import React, { useState } from 'react';
import { saveEstimate } from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency'; // Import the updated utility

function SaveEstimateModal({ isOpen, onClose, doors, doorCosts, totalCost, totalSquareFootage }) {
  // State to manage form inputs and success state
  const [formData, setFormData] = useState({
	firstName: '',
	email: '',
	timeline: '1-3 Months',
	marketingConsent: true,
  });
  const [isSuccess, setIsSuccess] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
	const { name, value, type, checked } = e.target;
	setFormData((prev) => ({
	  ...prev,
	  [name]: type === 'checkbox' ? checked : value,
	}));
  };

  // Handle form submission
  const handleSubmit = async (event) => {
	event.preventDefault();

	try {
	  // Prepare door details summary including Retail and Savings
	  const doorDetails = doors
		.map((door, index) => {
		  const costData = doorCosts.find((dc) => dc.id === door.id) || {};
		  const dimensions = `${door.widthFt}' ${door.widthIn}" x ${door.heightFt}' ${door.heightIn}"`;
		  const cost = costData.totalCost || totalCost / doors.length; // Ensure cost is included
		  const retail = costData.retailCost ? formatCurrency(costData.retailCost) : 'N/A';
		  const savings = costData.savings ? formatCurrency(costData.savings) : 'N/A';
		  return `Door ${index + 1}:
Type: ${door.doorType}
Dimensions: ${dimensions}
Retail: ${retail}
Savings: ${savings}
Cost: ${formatCurrency(cost)}`;
		})
		.join('\n\n'); // Add double line breaks between door entries for readability

	  const doorTypes = [...new Set(doors.map((door) => door.doorType.toLowerCase()))].join('/');

	  // Payload for saving to Airtable
	  const payload = {
		"First Name": formData.firstName,
		"Email": formData.email,
		"Email Checkbox": formData.marketingConsent,
		"Timeline": formData.timeline,
		"Door Details": doorDetails,
		"Door Count": doors.length,
		"Total Square Footage": totalSquareFootage,
		"Total Cost": formatCurrency(totalCost),
		"Door Types": doorTypes,
		"Source": "Estimate Calculator",
	  };

	  console.log('Submitting Estimate Payload:', payload);

	  // Call saveEstimate function
	  const response = await saveEstimate(payload);

	  console.log('Submission Successful:', response);
	  setIsSuccess(true);

	  // Reset form data
	  setFormData({
		firstName: '',
		email: '',
		timeline: '1-3 Months',
		marketingConsent: true,
	  });
	} catch (error) {
	  console.error('Submission Failed:', error.message);
	  alert('There was an error saving your estimate. Please try again.');
	}
  };

  // Handle modal background click to close
  const handleModalClick = (e) => {
	if (e.target.classList.contains('modal-overlay')) {
	  onClose();
	  setIsSuccess(false);
	}
  };

  if (!isOpen) return null;

  return (
	<div
	  className={`modal-overlay ${isOpen ? 'active' : ''}`}
	  onClick={handleModalClick}
	  style={{
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0,0,0,0.5)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000,
	  }}
	>
	  <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
		{!isSuccess ? (
		  <div id="estimateFormView">
			<h3 className="text-lg font-semibold mb-4">Save Your Estimate</h3>
			<form onSubmit={handleSubmit} className="space-y-4">
			  {/* First Name Field */}
			  <div>
				<label className="block text-sm font-medium text-gray-700 mb-1">
				  First Name
				</label>
				<input
				  type="text"
				  name="firstName"
				  required
				  value={formData.firstName}
				  onChange={handleChange}
				  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
			  </div>
			  {/* Email Field */}
			  <div>
				<label className="block text-sm font-medium text-gray-700 mb-1">
				  Email
				</label>
				<input
				  type="email"
				  name="email"
				  required
				  value={formData.email}
				  onChange={handleChange}
				  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
			  </div>
			  {/* Timeline Field */}
			  <div>
				<label className="block text-sm font-medium text-gray-700 mb-1">
				  What's the timeline of your project?
				</label>
				<select
				  name="timeline"
				  required
				  value={formData.timeline}
				  onChange={handleChange}
				  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
				>
				  <option value="ASAP">As soon as possible</option>
				  <option value="1-3 Months">1-3 Months</option>
				  <option value="3-6 Months">3-6 Months</option>
				  <option value="6+ Months">6+ Months</option>
				</select>
			  </div>
			  {/* Marketing Consent Checkbox */}
			  <div className="flex items-start">
				<input
				  type="checkbox"
				  name="marketingConsent"
				  id="marketingConsent"
				  checked={formData.marketingConsent}
				  onChange={handleChange}
				  className="mt-1 h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
				/>
				<label htmlFor="marketingConsent" className="ml-2 text-sm text-gray-600">
				  I agree to receive emails from GlassHaus, LLC. I can
				  unsubscribe at anytime.
				</label>
			  </div>
			  {/* Form Buttons */}
			  <div className="flex justify-end space-x-3 pt-4">
				<button
				  type="button"
				  onClick={() => {
					onClose();
					setIsSuccess(false);
				  }}
				  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
				>
				  Cancel
				</button>
				<button
				  type="submit"
				  className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
				>
				  Save Estimate
				</button>
			  </div>
			</form>
		  </div>
		) : (
		  /* Success View */
		  <div id="successView" className="text-center py-8">
			<div className="mb-6">
			  <svg
				className="mx-auto h-12 w-12 text-green-500"
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			  >
				<path
				  strokeLinecap="round"
				  strokeLinejoin="round"
				  strokeWidth="2"
				  d="M5 13l4 4L19 7"
				/>
			  </svg>
			</div>
			<h3 className="text-lg font-semibold mb-2">Success!</h3>
			<p className="text-gray-600 mb-6">
			  Your estimate has been saved successfully.
			</p>
			<button
			  onClick={() => {
				onClose();
				setIsSuccess(false);
			  }}
			  className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
			>
			  Close
			</button>
		  </div>
		)}
	  </div>
	</div>
  );
}

export default SaveEstimateModal;
